import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface MapPoint {
  lat: number;
  lng: number;
  label: string;
  color?: string;
}

// Mock entity locations for preview
const MOCK_POINTS: MapPoint[] = [
  { lat: 48.8566, lng: 2.3522, label: 'Paris', color: '#3b82f6' },
  { lat: 51.5074, lng: -0.1278, label: 'Londres', color: '#a855f7' },
  { lat: 40.7128, lng: -74.006, label: 'New York', color: '#ef4444' },
  { lat: 35.6762, lng: 139.6503, label: 'Tokyo', color: '#22c55e' },
  { lat: 55.7558, lng: 37.6173, label: 'Moscou', color: '#f97316' },
  { lat: 25.2048, lng: 55.2708, label: 'Dubai', color: '#06b6d4' },
  { lat: 1.3521, lng: 103.8198, label: 'Singapour', color: '#14b8a6' },
  { lat: -33.8688, lng: 151.2093, label: 'Sydney', color: '#eab308' },
];

// Simple Mercator projection
function project(lat: number, lng: number, width: number, height: number): { x: number; y: number } {
  const x = ((lng + 180) / 360) * width;
  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = height / 2 - (mercN * height) / (2 * Math.PI);
  return { x, y };
}

export default function MapWidget() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;

    // Clear
    ctx.fillStyle = '#0f1a2e';
    ctx.fillRect(0, 0, W, H);

    // Draw grid lines (simplified world)
    ctx.strokeStyle = 'rgba(30, 58, 95, 0.5)';
    ctx.lineWidth = 0.5;
    for (let lat = -60; lat <= 80; lat += 30) {
      const p = project(lat, -180, W, H);
      const p2 = project(lat, 180, W, H);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p2.x, p.y);
      ctx.stroke();
    }
    for (let lng = -180; lng <= 180; lng += 30) {
      ctx.beginPath();
      ctx.moveTo(((lng + 180) / 360) * W, 0);
      ctx.lineTo(((lng + 180) / 360) * W, H);
      ctx.stroke();
    }

    // Draw points
    for (const point of MOCK_POINTS) {
      const { x, y } = project(point.lat, point.lng, W, H);
      const color = point.color ?? '#3b82f6';

      // Glow
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, 12);
      gradient.addColorStop(0, color + '60');
      gradient.addColorStop(1, color + '00');
      ctx.beginPath();
      ctx.arc(x, y, 12, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Dot
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
  }, []);

  return (
    <div className="glass-panel p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-300">Carte des entites</h3>
        <button
          onClick={() => navigate('/graph')}
          className="text-xs text-cyber-cyan hover:underline"
        >
          Ouvrir
        </button>
      </div>
      <div
        className="flex-1 min-h-0 cursor-pointer rounded-lg overflow-hidden"
        onClick={() => navigate('/graph')}
      >
        <canvas
          ref={canvasRef}
          width={320}
          height={180}
          className="w-full h-full rounded-lg"
        />
      </div>
    </div>
  );
}
